import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, MessageCircle, Menu, X, Plane } from "lucide-react";
import { SITE, waLink } from "@/lib/site";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/packages?category=domestic", label: "Domestic" },
  { to: "/packages?category=international", label: "International" },
  { to: "/services", label: "Services" },
  { to: "/about", label: "About" },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname, location.search]);

  const onDark = location.pathname === "/" && !scrolled;

  return (
    <header className="fixed top-0 inset-x-0 z-50" data-testid="site-header">
      <div className="bg-ocean text-bone/90 text-xs tracking-wide">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2 flex items-center justify-between gap-4">
          <p data-testid="offer-strip">Monsoon Sale — save up to 25% on select packages this season</p>
          <a href={`tel:${SITE.phoneRaw}`} data-testid="topbar-phone" className="hidden sm:flex items-center gap-1.5 hover:text-sun transition-colors">
            <Phone size={12} /> {SITE.phoneDisplay}
          </a>
        </div>
      </div>
      <div className={`transition-colors duration-500 ${scrolled || !onDark ? "bg-bone/80 backdrop-blur-xl border-b border-ink/5 shadow-[inset_0_1px_0_rgba(255,255,255,0.6)]" : "bg-ink/30 backdrop-blur-md border-b border-white/10"}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-2">
            <span className={`inline-flex rounded-xl px-3 py-1.5 transition-colors ${onDark ? "bg-bone" : "bg-white shadow-sm border border-border"}`}>
              <img src="/logo.png" alt="click2trip — we Exchange Happiness" className="h-8 w-auto" />
            </span>
          </Link>
          <nav className="hidden lg:flex items-center gap-7" data-testid="main-nav">
            {NAV.map((n) => (
              <NavLink key={n.label} to={n.to} data-testid={`nav-${n.label.toLowerCase()}`}
                className={`text-sm font-medium transition-colors hover:text-sun ${onDark ? "text-bone/90" : "text-ink/80"}`}>
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <a href={`tel:${SITE.phoneRaw}`} data-testid="header-call-btn"
              className={`hidden md:flex items-center gap-2 text-sm font-semibold transition-colors ${onDark ? "text-bone" : "text-ink"} hover:text-sun`}>
              <Phone size={15} /> {SITE.phoneDisplay}
            </a>
            <a href={waLink("Hi click2trip! I'd like to plan a trip.")} target="_blank" rel="noreferrer"
              data-testid="header-whatsapp-btn"
              className="hidden sm:inline-flex items-center gap-2 bg-[#25D366] text-white text-sm font-semibold px-4 py-2 rounded-full hover:bg-[#1fb857] hover:scale-[1.03] active:scale-95 transition-all duration-200">
              <MessageCircle size={15} /> WhatsApp
            </a>
            <button onClick={() => setOpen(!open)} data-testid="mobile-menu-btn" aria-label="Menu"
              className={`lg:hidden p-2 ${onDark ? "text-bone" : "text-ink"}`}>
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
            className="lg:hidden bg-bone border-b border-border shadow-xl" data-testid="mobile-nav">
            <div className="px-6 py-4 flex flex-col gap-1">
              {NAV.map((n) => (
                <NavLink key={n.label} to={n.to} data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                  className="py-3 text-base font-medium text-ink/80 border-b border-border/50 last:border-0">
                  {n.label}
                </NavLink>
              ))}
              <a href={waLink("Hi click2trip! I'd like to plan a trip.")} target="_blank" rel="noreferrer"
                data-testid="mobile-whatsapp-btn"
                className="mt-3 inline-flex items-center justify-center gap-2 bg-[#25D366] text-white font-semibold px-4 py-3 rounded-full">
                <MessageCircle size={16} /> Chat on WhatsApp
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
